import Head from 'next/head';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import MLink from '@material-ui/core/Link';
import { buildTitle } from '../utils/layout';
import MaterialLink from '../components/material-link';
import config from '../config';
import Main from '../components/main';

const useStyles = makeStyles((theme) => ({
  tip: {
    marginTop: theme.spacing(2),
    fontStyle: 'italic',
  },
  guideList: {
    '& > li': {
      marginBottom: theme.spacing(2),
    }
  },
  googleDoc: {
    marginTop: theme.spacing(2),
  },
  headerBasic: {
    fontWeight: 'normal',
  },
}));

const GroupRideGuide = () => {
  const classes = useStyles();

  return (
    <Main>
      <Head>
        <title>{buildTitle('Group Ride Guide')}</title>
        <link rel="canonical" href={`${config().universal.baseUri}/group-ride-guide`} />
      </Head>

      <Typography variant="h1">Everyday Rides Group Ride Guide</Typography>

      <p>Hosting group rides can be a lot of fun. There’s nothing quite like bringing a group of people together and enjoying riding bikes together. With a little thought and preparation you can make your ride accessible and easy for riders to participate in.</p>

      <ol>
        <li>Set clear expectations ahead of time</li>
        <li>Support riders during the ride</li>
        <li>Have fun!</li>
      </ol>

      <Typography variant="h2">Set clear expectations ahead of time</Typography>

      <p>Not all rides are right for everybody and that’s okay. Especially for folks who may not already know you, people want to know what to expect from the ride so they can decide whether it’s right for them.</p>

      <ul className={classes.guideList}>
        <li>
          <Typography variant="h3">When does the ride start exactly?</Typography>
          <p>
            Setting a meeting and exact start time is arguably the most important thing you can do. A good strategy is to set a start time half an hour or more before the actual ride time so that people have an opportunity to resolve any mechanical issues and make up for delays in getting to the start. Setting an exact “we’re leaving without you” time is also key in making sure people can plan properly to get to the ride on time.
          </p>
          <div className={classes.tip}>
            Tip: Set the start time on Everyday Rides to the pre-ride time, not the actual depart time, but mention the actual depart time in the event listing.
          </div>
        </li>
        <li>
          <Typography variant="h3">Where does the ride start and end?</Typography>
          <p>
            If the ride does not start in a convenient location, how are riders expected to get to/from the start/finish? Does it end somewhere different from where it starts?
          </p>
          <div className={classes.tip}>
            Tip: Use the start and end location fields to make it easy for people to plan
          </div>
        </li>
        <li>
          <Typography variant="h3">What will the pace be?</Typography>
          <p>
            Do you want to ride fast and hard or do you want it to be inclusive of slower, less experienced riders? Will the ride stick together, will there be meetup points, or are you on your own?
          </p>
          <div className={classes.tip}>
            Tip: Mention pace and drop status in the ride description
          </div>
        </li>
        <li>
          <Typography variant="h3">How long is the route?</Typography>
          <p>
            Share important metrics like mileage and elevation gain so people know whether the ride is right for them.
          </p>
          <div className={classes.tip}>
            Tip: Add a route in the “Route URL” field on your event
          </div>
        </li>
        <li>
          <Typography variant="h3">What’s the terrain like?</Typography>
          <p>
            Will this be a road ride or will there be gravel or single track or even hike-a-bike situations?
          </p>
          <div className={classes.tip}>
            Tip: Use categories to specify the terrain of the ride
          </div>
        </li>
        <li>
          <Typography variant="h3">Does the ride require special equipment or clothing?</Typography>
          <p>
            What sort of bike, gearing, or tires are ideal for the ride? Is there weather expected that might not be obvious for riders to prepare for? What information to provide here is up to you, but depending on who you’re expecting to join for the ride, this information could be crucial to make sure people don’t come unprepared.
          </p>
          <div className={classes.tip}>
            Tip: Call this out in the ride description if you think this is important
          </div>
        </li>
        <li>
          <Typography variant="h3">Are services unavailable along the route?</Typography>
          <p>
            If the ride is planning to go extended amounts of time without services or service stops, people may need to be especially prepared or choose not to ride if the circumstances are extenuating. In my experience this type of information is often overlooked by people who aren’t planning the route.
          </p>
          <div className={classes.tip}>
            Tip: Mention this in the ride description if you think this is important
          </div>
        </li>
      </ul>

      <Typography variant="h2">Support riders during the ride</Typography>

      <p>No matter the ride, setting expectations is the most important thing to ensure riders choose wisely whether to join your ride and are prepared when they show up.</p>

      <p>Ways to support riders:</p>

      <ul className={classes.guideList}>
        <li>
          <Typography variant="h3">Post the route ahead of time</Typography>
          <p>Sometimes people will trust you and go on an adventure, but often times people have other responsibilities to get to, can’t make the start, or just need to mentally prepare for the ride they’re going on. Posting the route or at least mileage and elevation statistics ahead of time can put potential riders at ease and make your ride more accessible.</p>
          <div className={classes.tip}>
            Tip: Ride with GPS is a great tool for creating GPS routes and if you add it to your event it will automatically embed a map of the route
          </div>
        </li>
        <li>
          <Typography variant="h3">Lay out guidelines for how riders can stay connected during the ride</Typography>
          <p>
            Sharing your phone number or information about how to get in contact with the ride if people get lost can also be helpful in case of mechanical failure, but can be a great way to ensure people have a contact in case a safety issue comes up.
          </p>
          <p>
            During the ride, a great way of helping others keep track of the ride progress is to use Google Maps Location Sharing or Glympse to create a temporary link where people can watch your real time location while the ride is happening.
          </p>
        </li>
        <li>
          <Typography variant="h3">Inform participants about regroup efforts</Typography>
          <p>
            In order to tame the anxieties of riders, let them know ahead of time where to expect or not expect regrouping. This could be just where and when the group plans to stop for services. If there are no efforts, saying so can be just as important so people make sure they know the route or how to bail.
          </p>
          <p>
            For social rides in the city, especially those welcoming slower or less experienced riders, it can be helpful to explain processes for waiting at intersections and if someone is designated to take up the rear of the ride. These efforts will ease anxieties and make sure that riders don’t get left behind.
          </p>
          <p>
            If the ride is a &quot;no drop&quot; ride intended to keep all cyclists together, a successful technique is to designate a dedicated cyclist who knows the route and plan to ride at the back of the ride and &quot;sweep&quot; to make sure everyone is able to stay with the group and doesn&apos;t get lost or abandoned. This is extremely helpful for slower riders, for riders who need to stop to rest, for riders who experience mechanical failures, or any unexpected issue that might arise.
          </p>
        </li>
        <li>
          <Typography variant="h3">Share emergency contact information</Typography>
          <p>For rides without services, let people know how they should handle emergencies. For backcountry trips and multi-day trips especially, it’s a good idea to share major allergies and emergency contact information between everyone in the group.</p>
        </li>
      </ul>

      <Typography variant="h2">Route Planning Resources</Typography>

      <ul>
        <li>
          <Typography variant="h3">
            <MLink href="https://ridewithgps.com/" rel="noopener noreferrer">Ride With GPS</MLink>
          </Typography>
          <p>
            Use the route finder, check out routes designed by <MLink href="https://ridewithgps.com/ambassadors" rel="noopener noreferrer">area ambassadors</MLink>, and check out ride reports.
          </p>
        </li>
        <li>
          <Typography variant="h3">
            <MLink href="https://www.komoot.com/" rel="noopener noreferrer">Komoot</MLink>
          </Typography>
          <p>
            The routes can be hard to find, but in the footer you can click to browse different types of routes such as <MLink href="https://www.komoot.com/discover/cycling-routes" rel="noopener noreferrer">cycling routes</MLink>.
          </p>
        </li>
        <li>
          <Typography variant="h3">
            <MLink href="https://www.strava.com/heatmap" rel="noopener noreferrer">Strava Heat Map</MLink>
          </Typography>
          <p>
            This can be immensely helpful when you’re trying to figure out which roads to take. Roads where people have ridden will be highlighted and usually suggest better riding or passable areas.
          </p>
        </li>
        <li>
          <Typography variant="h3">
            <MLink href="https://en-us.topographic-map.com/maps/na3/Seattle/" rel="noopener noreferrer">Topographic Maps</MLink> <span className={classes.headerBasic}>and</span> <MLink href="https://www.accessmap.io/?region=wa.seattle&lon=-122.338&lat=47.607&z=14.5" rel="noopener noreferrer">Access Map</MLink>
          </Typography>
          <p>
            Tools for assessing street grades to try to find or avoid roads that are too steep.
          </p>
        </li>
        <li>
          <Typography variant="h3">
            <MLink href="https://bikepacking.com/bikepacking-routes/" rel="noopener noreferrer">Bikepacking.com routes</MLink>
          </Typography>
          <p>
            The ratings for skill level and number of days can be hit or miss, but Bikepacking.com has a great directory of excellent bikepacking routes.
          </p>
        </li>
        <li>
          <Typography variant="h3">
            <MLink href="http://www.omtm.cc/" rel="noopener noreferrer">OMTM</MLink>
          </Typography>
          <p>These folks have curated a whole bunch of excellent routes in the PNW, specializing in gravel rides.</p>
        </li>
        <li>
          <Typography variant="h3">
            <MaterialLink href="/">Everyday Rides</MaterialLink>
          </Typography>
          <p>Browse past rides for ideas and reach out to the ride hosts if you have any questions.</p>
        </li>
      </ul>
    </Main>
  );
};

export default GroupRideGuide;
